import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Message } from 'elements/minute/sidebar/sidebarComponents/atomic/Message';
import { SearchValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SearchValue';
import { SidebarGrid } from 'elements/minute/sidebar/sidebarComponents/complex/SidebarGrid/SidebarGrid';
import { useDragAndDropImage } from 'hooks/map/useDragAndDropImage';
import { getFontAwesomeIcons } from 'libs/images/FontAwesomeGalleryData';
import { useState } from 'react';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    selectedImageUrl?: string;
    color?: string;
    initialRowsVisible?: number;
    dragAndDropEnabled?: boolean;
    allowEmpty?: boolean;
    onSelected?: (params: { imageUrl: string; color?: string }) => void;
}

const ALL_ICONS = [...getFontAwesomeIcons()];

export const SymbolGallery = (props: Props): JSX.Element => {
    const { selectedImageUrl } = props;
    const dragAndDropImage = useDragAndDropImage('icon');

    const [searchText, setSearchText] = useState('');

    const filteredSymbols =
        searchText.length >= 3
            ? ALL_ICONS.filter((symbol) =>
                  symbol.keywords?.some((keyword) => keyword.includes(searchText))
              )
            : ALL_ICONS;

    const symbolUrls = filteredSymbols.map((e) => e.imageUrl);
    const options = props.allowEmpty ? [null, ...symbolUrls] : symbolUrls;
    const selectedIndex = options.findIndex((e) => e === selectedImageUrl);

    return (
        <S_SymbolGallery className={props.className}>
            <S_SearchValue
                onChange={(newText) => {
                    setSearchText(newText);
                }}
            />

            {options?.length > 0 && (
                <SidebarGrid
                    scrollRef={dragAndDropImage.containerRef}
                    initialRowsVisible={props.initialRowsVisible}
                    items={options.map((symbolUrl) => {
                        if (symbolUrl === null) {
                            return (
                                <S_Wrap>
                                    <S_Transparent
                                        key={'empty'}
                                        icon={['fal', 'circle-half-stroke']}
                                    />
                                </S_Wrap>
                            );
                        }
                        if (symbolUrl?.includes('.svg')) {
                            return (
                                <S_ColoredSvg
                                    key={symbolUrl}
                                    src={symbolUrl}
                                    draggable={props.dragAndDropEnabled ?? false}
                                    onDragStart={(event) => {
                                        dragAndDropImage.onDragStart({
                                            event,
                                            imageUrl: symbolUrl
                                        });
                                    }}
                                    onDragEnd={() => {
                                        dragAndDropImage.onDragEnd();
                                    }}
                                />
                            );
                        } else {
                            return (
                                <S_Image
                                    key={symbolUrl}
                                    src={symbolUrl}
                                    draggable={props.dragAndDropEnabled ?? false}
                                    onDragStart={(event) => {
                                        dragAndDropImage.onDragStart({
                                            event,
                                            imageUrl: symbolUrl
                                        });
                                    }}
                                    onDragEnd={() => {
                                        dragAndDropImage.onDragEnd();
                                    }}
                                />
                            );
                        }
                    })}
                    columns={6}
                    selectedIndex={selectedIndex}
                    onClick={(index) => {
                        props.onSelected?.({ imageUrl: options[index], color: null });
                    }}
                />
            )}

            {!options.length && <Message type="info">{Localization.NOTHING_FOUND}</Message>}
        </S_SymbolGallery>
    );
};

const S_SymbolGallery = styled.div`
    position: relative;
`;

const S_SearchValue = styled(SearchValue)`
    margin-bottom: 15px;
`;

const S_Image = styled.img`
    width: 45px;
    height: 45px;
    padding: 9px;
`;

const S_ColoredSvg = styled.img`
    width: 45px;
    height: 45px;
    padding: 9px;
    filter: invert(0.25) sepia(0.1) saturate(200%) hue-rotate(520deg);
`;

const S_Transparent = styled(FontAwesomeIcon)`
    width: 20px;
    height: 20px;
    font-size: 20px;
    color: #141c54;
    transform: rotate(315deg);
`;

const S_Wrap = styled.div`
    min-width: 45px;
    min-height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
`;
